import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { Calendar, MapPin, Ticket, Users, CheckCircle, Tag, X } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import "./Events.css";
import api from "../api";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function Portal({ children }) {
  return ReactDOM.createPortal(children, document.body);
}

function PackageSelector({ packages, selectedPackage, onSelect, onClear }) {
  return (
    <div className="package-selector">
      <h4 className="package-selector-title">🎫 Choose a Package</h4>
      <p className="package-selector-sub">
        Select a named group package below, or scroll down to buy individual tickets.
      </p>
      <div className="packages-grid">
        {packages.map(pkg => (
          <button
            key={pkg.id}
            type="button"
            className={`package-card ${selectedPackage?.id === pkg.id ? "selected" : ""} ${pkg.is_sold_out ? "sold-out" : ""}`}
            onClick={() => !pkg.is_sold_out && onSelect(pkg)}
            disabled={pkg.is_sold_out}
          >
            <div className="package-name">{pkg.name}</div>
            <div className="package-size">{pkg.size} people</div>
            <div className="package-price">${parseFloat(pkg.price).toFixed(2)} AUD</div>
            <div className="package-per-person">${pkg.price_per_person.toFixed(2)} per person</div>
            {pkg.description && <div className="package-description">{pkg.description}</div>}
            {pkg.is_sold_out && <div className="package-sold-out">Sold Out</div>}
            {selectedPackage?.id === pkg.id && <div className="package-selected-tick">✓ Selected</div>}
          </button>
        ))}
      </div>
      {selectedPackage && (
        <button type="button" className="clear-package-btn" onClick={onClear}>
          ✕ Remove package — buy individual tickets instead
        </button>
      )}
      <div className="package-divider">
        <span>— or buy individual tickets below —</span>
      </div>
    </div>
  );
}

function AttendeeFields({ quantity, attendees, onChange }) {
  return (
    <div className="attendees-section">
      <h4 className="attendees-title">
        👥 Attendee Details ({quantity} {quantity === 1 ? "person" : "people"})
      </h4>
      <p className="attendee-booker-note">
        #1 is pre-filled from your booking contact details above.
      </p>
      {attendees.map((attendee, i) => (
        <div key={i} className="attendee-row">
          <div className="attendee-number">
            {i === 0 ? "📋 #1" : `#${i + 1}`}
          </div>
          <div className="attendee-fields">
            <div className="ticket-form-row">
              <div className="ticket-field">
                <label>First Name</label>
                <input
                  value={attendee.first_name}
                  onChange={e => onChange(i, 'first_name', e.target.value)}
                  placeholder="First name"
                  className={attendee._error_first ? "error" : ""}
                  readOnly={i === 0}
                  style={i === 0 ? { background: "#f5f5f5", color: "#666" } : {}}
                />
                {attendee._error_first && <span className="err">{attendee._error_first}</span>}
              </div>
              <div className="ticket-field">
                <label>Last Name</label>
                <input
                  value={attendee.last_name}
                  onChange={e => onChange(i, 'last_name', e.target.value)}
                  placeholder="Last name"
                  className={attendee._error_last ? "error" : ""}
                  readOnly={i === 0}
                  style={i === 0 ? { background: "#f5f5f5", color: "#666" } : {}}
                />
                {attendee._error_last && <span className="err">{attendee._error_last}</span>}
              </div>
            </div>
            <div className="ticket-field">
              <label>Email {i === 0 ? "" : "(optional)"}</label>
              <input
                type="email"
                value={attendee.email}
                onChange={e => onChange(i, 'email', e.target.value)}
                placeholder={i === 0 ? "email@example.com" : "email@example.com (optional)"}
                className={attendee._error_email ? "error" : ""}
                readOnly={i === 0}
                style={i === 0 ? { background: "#f5f5f5", color: "#666" } : {}}
              />
              {attendee._error_email && <span className="err">{attendee._error_email}</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function PricingTiers({ event }) {
  if (!event.tier2_min && !event.tier3_min) return null;
  return (
    <div className="pricing-tiers">
      <p className="pricing-tiers-title"><Tag size={14} /> Group Discounts</p>
      <div className="pricing-tiers-grid">
        <div className="tier-row">
          <span>1{event.tier2_min ? `–${event.tier2_min - 1}` : "+"} tickets</span>
          <span>${parseFloat(event.ticket_price).toFixed(2)} each</span>
          <span className="tier-tag">Standard</span>
        </div>
        {event.tier2_min && (
          <div className="tier-row">
            <span>{event.tier2_min}{event.tier3_min ? `–${event.tier3_min - 1}` : "+"} tickets</span>
            <span>${(parseFloat(event.ticket_price) * (1 - parseFloat(event.tier2_discount) / 100)).toFixed(2)} each</span>
            <span className="tier-tag green">{event.tier2_discount}% off</span>
          </div>
        )}
        {event.tier3_min && (
          <div className="tier-row">
            <span>{event.tier3_min}+ tickets</span>
            <span>${(parseFloat(event.ticket_price) * (1 - parseFloat(event.tier3_discount) / 100)).toFixed(2)} each</span>
            <span className="tier-tag gold">{event.tier3_discount}% off</span>
          </div>
        )}
      </div>
    </div>
  );
}

function TicketForm({ event, onClose, onSuccess }) {
  const stripe   = useStripe();
  const elements = useElements();

  const [selectedPackage, setSelectedPackage] = useState(null);
  const [formData, setFormData] = useState({
    first_name: "", last_name: "", email: "", phone: "", quantity: 1,
  });

  const emptyAttendee = () => ({
    first_name: "", last_name: "", email: "",
    _error_first: "", _error_last: "", _error_email: "",
  });

  const [attendees, setAttendees] = useState([emptyAttendee()]);
  const [errors,    setErrors]    = useState({});
  const [loading,   setLoading]   = useState(false);
  const [cardError, setCardError] = useState("");
  const [pricing,   setPricing]   = useState({
    unitPrice  : parseFloat(event.ticket_price || 0),
    discountPct: 0,
    total      : parseFloat(event.ticket_price || 0),
  });

  useEffect(() => {
    const w = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${w}px`;
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, []);

  const syncAttendeeOne = (field, value) => {
    setAttendees(prev => {
      const updated = [...prev];
      updated[0] = { ...updated[0], [field]: value };
      return updated;
    });
  };

  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
    const qty = pkg.size;
    setPricing({
      unitPrice  : pkg.price_per_person,
      discountPct: 0,
      total      : parseFloat(pkg.price),
    });
    setFormData(prev => ({ ...prev, quantity: qty }));
    setAttendees(prev => {
      const newList = Array(qty).fill(null).map(emptyAttendee);
      newList[0] = {
        ...emptyAttendee(),
        first_name: formData.first_name,
        last_name : formData.last_name,
        email     : formData.email,
      };
      return newList;
    });
  };

  const handleClearPackage = () => {
    setSelectedPackage(null);
    updateQuantity(1);
  };

  const updateQuantity = (qty) => {
    if (selectedPackage) return;
    const q = Math.max(1, Math.min(50, parseInt(qty) || 1));
    let discountPct = 0;
    if (event.tier3_min && q >= event.tier3_min && event.tier3_discount)
      discountPct = parseFloat(event.tier3_discount);
    else if (event.tier2_min && q >= event.tier2_min && event.tier2_discount)
      discountPct = parseFloat(event.tier2_discount);
    const base      = parseFloat(event.ticket_price);
    const unitPrice = parseFloat((base * (1 - discountPct / 100)).toFixed(2));
    const total     = parseFloat((unitPrice * q).toFixed(2));
    setPricing({ unitPrice, discountPct, total });
    setFormData(prev => ({ ...prev, quantity: q }));
    setAttendees(prev => {
      if (q > prev.length)
        return [...prev, ...Array(q - prev.length).fill(null).map(emptyAttendee)];
      return prev.slice(0, q);
    });
  };

  const handleAttendeeChange = (index, field, value) => {
    if (index === 0) return;
    setAttendees(prev => {
      const updated = [...prev];
      const key = field === 'first_name' ? '_error_first'
                : field === 'last_name'  ? '_error_last'
                : '_error_email';
      updated[index] = { ...updated[index], [field]: value, [key]: "" };
      return updated;
    });
  };

  const validate = () => {
    const e = {};
    if (!formData.first_name.trim()) e.first_name = "Required";
    if (!formData.last_name.trim())  e.last_name  = "Required";
    if (!formData.email.trim())      e.email      = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      e.email = "Invalid email";
    setErrors(e);

    let attendeesValid = true;
    const updated = attendees.map((a, i) => {
      const errs = { _error_first: "", _error_last: "", _error_email: "" };
      if (!a.first_name.trim()) { errs._error_first = "Required"; attendeesValid = false; }
      if (!a.last_name.trim())  { errs._error_last  = "Required"; attendeesValid = false; }
      if (i > 0 && a.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a.email)) {
        errs._error_email = "Invalid email format"; attendeesValid = false;
      }
      return { ...a, ...errs };
    });
    setAttendees(updated);
    return Object.keys(e).length === 0 && attendeesValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || !stripe || !elements) return;
    setLoading(true);
    setCardError("");

    const cleanAttendees = attendees.map(
      ({ _error_first, _error_last, _error_email, ...a }) => a
    );

    try {
      const intentResp = await api.post("events/tickets/create-intent/", {
        event_id  : event.id,
        package_id: selectedPackage?.id || null,
        first_name: formData.first_name,
        last_name : formData.last_name,
        email     : formData.email,
        phone     : formData.phone,
        quantity  : formData.quantity,
      });

      const { client_secret, payment_intent_id } = intentResp.data;

      const result = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name : `${formData.first_name} ${formData.last_name}`,
            email: formData.email,
          },
        },
      });

      if (result.error) {
        setCardError(result.error.message);
        setLoading(false);
        return;
      }

      const confirmResp = await api.post("events/tickets/confirm/", {
        payment_intent_id,
        event_id  : event.id,
        package_id: selectedPackage?.id || null,
        first_name: formData.first_name,
        last_name : formData.last_name,
        email     : formData.email,
        phone     : formData.phone,
        quantity  : formData.quantity,
        attendees : cleanAttendees,
      });

      onSuccess({
        ...confirmResp.data,
        buyer_name : `${formData.first_name} ${formData.last_name}`,
        buyer_email: formData.email,
        attendees  : cleanAttendees,
      });

    } catch (err) {
      setCardError(err.response?.data?.error || "Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const activePackages = (event.packages || []).filter(p => p.is_active);

  return (
    <Portal>
      <div className="ticket-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="ticket-modal">
          <button className="ticket-close" onClick={onClose}>✕</button>

          <div className="ticket-modal-header">
            <Ticket size={24} />
            <h2>Buy Tickets</h2>
          </div>

          <div className="ticket-event-summary">
            <h3>{event.title}</h3>
            <p><Calendar size={14} /> {event.date}{event.time ? ` at ${event.time}` : ""}</p>
            <p><MapPin size={14} /> {event.location || "TBC"}</p>
            {event.ticket_price && (
              <p className="ticket-price-badge">${event.ticket_price} AUD per ticket</p>
            )}
          </div>

          {activePackages.length > 0 && (
            <PackageSelector
              packages={activePackages}
              selectedPackage={selectedPackage}
              onSelect={handleSelectPackage}
              onClear={handleClearPackage}
            />
          )}

          {!selectedPackage && <PricingTiers event={event} />}

          <form onSubmit={handleSubmit} className="ticket-form">

            {selectedPackage && (
              <div className="selected-package-banner">
                🎫 <strong>{selectedPackage.name}</strong> — {selectedPackage.size} people
                at ${parseFloat(selectedPackage.price).toFixed(2)} AUD
              </div>
            )}

            <div className="lead-booker-section">
              <h4 className="attendees-title">📋 Booking Contact</h4>
              <div className="ticket-form-row">
                <div className="ticket-field">
                  <label>First Name</label>
                  <input
                    value={formData.first_name}
                    onChange={e => {
                      const val = e.target.value;
                      setFormData(p => ({ ...p, first_name: val }));
                      if (errors.first_name) setErrors(p => ({ ...p, first_name: "" }));
                      syncAttendeeOne('first_name', val);
                    }}
                    className={errors.first_name ? "error" : ""}
                  />
                  {errors.first_name && <span className="err">{errors.first_name}</span>}
                </div>
                <div className="ticket-field">
                  <label>Last Name</label>
                  <input
                    value={formData.last_name}
                    onChange={e => {
                      const val = e.target.value;
                      setFormData(p => ({ ...p, last_name: val }));
                      if (errors.last_name) setErrors(p => ({ ...p, last_name: "" }));
                      syncAttendeeOne('last_name', val);
                    }}
                    className={errors.last_name ? "error" : ""}
                  />
                  {errors.last_name && <span className="err">{errors.last_name}</span>}
                </div>
              </div>
              <div className="ticket-form-row">
                <div className="ticket-field">
                  <label>Email (confirmation sent here)</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => {
                      const val = e.target.value;
                      setFormData(p => ({ ...p, email: val }));
                      if (errors.email) setErrors(p => ({ ...p, email: "" }));
                      syncAttendeeOne('email', val);
                    }}
                    className={errors.email ? "error" : ""}
                  />
                  {errors.email && <span className="err">{errors.email}</span>}
                </div>
                <div className="ticket-field">
                  <label>Phone (optional)</label>
                  <input
                    value={formData.phone}
                    onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                    placeholder="+61 400 000 000"
                  />
                </div>
              </div>
            </div>

            {!selectedPackage && (
              <div className="quantity-row">
                <div className="ticket-field">
                  <label>Number of Tickets</label>
                  <div className="quantity-input-row">
                    <button type="button" className="qty-btn"
                      onClick={() => updateQuantity((parseInt(formData.quantity) || 1) - 1)}>
                      −
                    </button>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formData.quantity}
                      onChange={e => {
                        const val = e.target.value.replace(/[^0-9]/g, "");
                        if (val === "") {
                          setFormData(p => ({ ...p, quantity: "" }));
                        } else {
                          updateQuantity(parseInt(val));
                        }
                      }}
                      onBlur={() => {
                        if (!formData.quantity || formData.quantity < 1) updateQuantity(1);
                      }}
                      className="qty-input"
                    />
                    <button type="button" className="qty-btn"
                      onClick={() => updateQuantity((parseInt(formData.quantity) || 1) + 1)}>
                      +
                    </button>
                  </div>
                </div>
                <div className="price-summary">
                  {pricing.discountPct > 0 && (
                    <div className="discount-badge">
                      🎉 {pricing.discountPct}% group discount applied!
                    </div>
                  )}
                  <div className="ticket-total">
                    <span>{formData.quantity} × ${pricing.unitPrice.toFixed(2)}</span>
                    <strong>${pricing.total.toFixed(2)} AUD</strong>
                  </div>
                </div>
              </div>
            )}

            {selectedPackage && (
              <div className="ticket-total" style={{ marginBottom: "1rem" }}>
                <span>{selectedPackage.size} people × ${selectedPackage.price_per_person.toFixed(2)}</span>
                <strong>${parseFloat(selectedPackage.price).toFixed(2)} AUD</strong>
              </div>
            )}

            <AttendeeFields
              quantity={formData.quantity}
              attendees={attendees}
              onChange={handleAttendeeChange}
            />

            <div className="ticket-field" style={{ marginTop: "1rem" }}>
              <label>Card Details</label>
              <div className="stripe-card-wrapper">
                <CardElement options={{
                  hidePostalCode: true,
                  style: {
                    base:    { fontSize: "16px", color: "#333", "::placeholder": { color: "#aaa" } },
                    invalid: { color: "#e53935" },
                  }
                }} />
              </div>
              {cardError && <span className="err">{cardError}</span>}
            </div>

            <p className="ticket-secure-note">🔒 Secured by Stripe. Your card details are encrypted.</p>

            <button type="submit" className="ticket-pay-btn" disabled={loading || !stripe}>
              {loading ? "Processing…" : `Pay $${selectedPackage ? parseFloat(selectedPackage.price).toFixed(2) : pricing.total.toFixed(2)} AUD`}
            </button>
          </form>
        </div>
      </div>
    </Portal>
  );
}

function TicketSuccess({ data, onClose }) {
  useEffect(() => {
    const w = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${w}px`;
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, []);

  return (
    <Portal>
      <div className="ticket-overlay">
        <div className="ticket-modal ticket-success-modal">
          <CheckCircle size={60} color="#28a745"
            style={{ margin: "0 auto 1rem", display: "block" }} />
          <h2>Tickets Confirmed! 🎉</h2>
          <p>Hi <strong>{data.buyer_name}</strong>, your booking is confirmed.</p>

          <div className="ticket-success-details">
            <div><span>Event</span><strong>{data.event_title}</strong></div>
            <div><span>Date</span><strong>{data.event_date}</strong></div>
            {data.package_name && (
              <div><span>Package</span><strong>{data.package_name}</strong></div>
            )}
            <div><span>Tickets</span><strong>{data.quantity}</strong></div>
            {data.discount_pct > 0 && (
              <div><span>Discount</span><strong className="green-text">{data.discount_pct}% off</strong></div>
            )}
            <div><span>Total Paid</span><strong>${parseFloat(data.total_amount).toFixed(2)} AUD</strong></div>
            <div><span>Reference</span><strong className="ticket-ref">{data.ticket_reference}</strong></div>
          </div>

          {data.attendees && data.attendees.length > 0 && (
            <div className="attendees-summary">
              <p className="attendees-summary-title">Attendees:</p>
              {data.attendees.map((a, i) => (
                <div key={i} className="attendee-summary-row">
                  <span>{i + 1}. {a.first_name} {a.last_name}</span>
                  <span>{a.email}</span>
                </div>
              ))}
            </div>
          )}

          <p className="ticket-email-note">
            Confirmation sent to <strong>{data.buyer_email}</strong>.
            Show your reference at the event.
          </p>
          <button className="ticket-close-btn" onClick={onClose}>Done</button>
        </div>
      </div>
    </Portal>
  );
}

// ── Event Detail Modal ───────────────────────────────────────────────────────
function EventDetailModal({ event, onClose, onBuyTicket, past }) {
  useEffect(() => {
    const w = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${w}px`;
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, []);

  const resolveImageUrl = (image) => {
    if (!image) return "/placeholder.jpg";
    if (image.startsWith("http")) return image;
    return `${process.env.REACT_APP_API_URL.replace("/api/", "")}${image}`;
  };

  const hasPackages = (event.packages || []).filter(p => p.is_active).length > 0;

  return (
    <Portal>
      <div className="ticket-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="event-detail-modal">
          <button className="ticket-close" onClick={onClose}>
            <X size={18} />
          </button>

          <div className="event-detail-image">
            <img src={resolveImageUrl(event.image)} alt={event.title} />
            {event.is_paid_event && !past && (
              <div className="event-ticket-badge">🎟 Ticketed Event</div>
            )}
            {hasPackages && !past && (
              <div className="event-discount-badge">🎫 Group Packages</div>
            )}
          </div>

          <div className="event-detail-body">
            <h2 className="event-detail-title">{event.title}</h2>

            <div className="event-detail-meta">
              <div><Calendar size={16} /><span>{event.date}{event.time ? ` · ${event.time}` : ""}</span></div>
              <div><MapPin size={16} /><span>{event.location || "TBC"}</span></div>
              {event.is_paid_event && event.ticket_price && (
                <div><Ticket size={16} /><span>${event.ticket_price} AUD per ticket</span></div>
              )}
              {event.is_paid_event && event.total_tickets && (
                <div>
                  <Users size={16} />
                  <span>
                    {event.is_sold_out
                      ? <span className="sold-out-text">Sold Out</span>
                      : `${event.tickets_remaining} tickets left`}
                  </span>
                </div>
              )}
              {hasPackages && (
                <div>
                  <Ticket size={16} />
                  <span>{(event.packages || []).filter(p => p.is_active).length} group packages available</span>
                </div>
              )}
            </div>

            {event.description && (
              <div className="event-detail-description">
                <h4>About this event</h4>
                <p>{event.description}</p>
              </div>
            )}

            {!past && (
              <div className="event-detail-actions">
                {event.is_paid_event ? (
                  <button
                    className={`ticket-pay-btn${event.is_sold_out ? " sold-out" : ""}`}
                    onClick={() => { onClose(); onBuyTicket(); }}
                    disabled={event.is_sold_out}
                  >
                    {event.is_sold_out ? "Sold Out" : "🎟 Buy Ticket"}
                  </button>
                ) : (
                  <p className="free-event-note" style={{ textAlign: "center", fontSize: "1rem" }}>
                    ✅ Free event — all welcome, just show up!
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
}

function EventCard({ event, past }) {
  const navigate     = useNavigate();
  const [showTicket,   setShowTicket]   = useState(false);
  const [ticketResult, setTicketResult] = useState(null);
  const [copied,       setCopied]       = useState(false);

  const ticketLink = `${window.location.origin}/events/${event.slug}?buy=1`;
  const copyTicketLink = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(ticketLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const resolveImageUrl = (image) => {
    if (!image) return "/placeholder.jpg";
    if (image.startsWith("http")) return image;
    return `${process.env.REACT_APP_API_URL.replace("/api/", "")}${image}`;
  };

  const hasPackages = (event.packages || []).filter(p => p.is_active).length > 0;

  return (
    <>
      {/* Entire card is clickable — navigates to dedicated event page */}
      <div
        className={`event-card${past ? " past" : ""}`}
        onClick={() => navigate(`/events/${event.slug}`)}
        style={{ cursor: "pointer" }}
      >
        <div className="event-image">
          <img src={resolveImageUrl(event.image)} alt={event.title} />
          {event.is_paid_event && !past && (
            <div className="event-ticket-badge">🎟 Ticketed Event</div>
          )}
          {hasPackages && !past && (
            <div className="event-discount-badge">🎫 Group Packages</div>
          )}
        </div>

        <div className="event-content">
          <h3>{event.title}</h3>
          <div className="event-details">
            <div><Calendar size={16} />{event.date}{event.time ? ` · ${event.time}` : ""}</div>
            <div><MapPin size={16} />{event.location || "TBC"}</div>
            {event.is_paid_event && event.ticket_price && (
              <div><Ticket size={16} />${event.ticket_price} AUD per ticket</div>
            )}
            {event.is_paid_event && event.total_tickets && (
              <div>
                <Users size={16} />
                {event.is_sold_out
                  ? <span className="sold-out-text">Sold Out</span>
                  : `${event.tickets_remaining} tickets left`}
              </div>
            )}
            {hasPackages && (
              <div>
                <Ticket size={16} />
                {(event.packages || []).filter(p => p.is_active).length} group packages available
              </div>
            )}
          </div>

          {/* Truncated description with Read more */}
          <p className="event-description event-description-clamp">{event.description}</p>
          {event.description && event.description.length > 120 && (
            <span className="read-more-link">Read more →</span>
          )}

          {event.is_paid_event && !past && (
            <div className="event-card-actions">
              <button
                className={`ticket-btn${event.is_sold_out ? " sold-out" : ""}`}
                onClick={e => {
                  e.stopPropagation();
                  !event.is_sold_out && setShowTicket(true);
                }}
                disabled={event.is_sold_out}
              >
                {event.is_sold_out ? "Sold Out" : "Buy Ticket"}
              </button>
              <button
                className="event-share-btn"
                onClick={copyTicketLink}
                title={ticketLink}
              >
                {copied ? "✅ Copied!" : "🔗 Copy Ticket Link"}
              </button>
            </div>
          )}
          {!event.is_paid_event && !past && (
            <div className="event-card-actions">
              <p className="free-event-note">✅ Free event — all welcome</p>
              <button
                className="event-share-btn"
                onClick={copyTicketLink}
                title={ticketLink}
              >
                {copied ? "✅ Copied!" : "🔗 Copy Event Link"}
              </button>
            </div>
          )}
        </div>
      </div>

      {showTicket && !ticketResult && (
        <Elements stripe={stripePromise}>
          <TicketForm
            event={event}
            onClose={() => setShowTicket(false)}
            onSuccess={result => { setShowTicket(false); setTicketResult(result); }}
          />
        </Elements>
      )}

      {ticketResult && (
        <TicketSuccess
          data={ticketResult}
          onClose={() => setTicketResult(null)}
        />
      )}
    </>
  );
}

export default function Events() {
  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("events/")
      .then(res => setEvents(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const today          = new Date();
  const upcomingEvents = events.filter(e => new Date(e.date) >= today);
  const pastEvents     = events.filter(e => new Date(e.date) <  today);

  if (loading) return <p style={{ textAlign: "center", padding: "2rem" }}>Loading events…</p>;

  return (
    <div className="events-container">
      <h1>Community Events</h1>
      <p className="events-intro">
        Stay updated with the latest community gatherings. Buy tickets directly
        for paid events or just show up for free ones.
      </p>

      <h2 className="section-title">Upcoming Events</h2>
      <div className="events-grid">
        {upcomingEvents.length > 0
          ? upcomingEvents.map(e => <EventCard key={e.id} event={e} />)
          : <p className="no-events">No upcoming events at the moment.</p>}
      </div>

      <h2 className="section-title">Past Events</h2>
      <div className="events-grid">
        {pastEvents.length > 0
          ? pastEvents.map(e => <EventCard key={e.id} event={e} past />)
          : <p className="no-events">No past events available.</p>}
      </div>
    </div>
  );
}